import React from 'react';

/**
 * Detailed Telemetry Panel - Stitch style telemetry cards for the selected drone.
 * Shows position/navigation, attitude, power & battery, and link & GPS.
 */
function DetailedTelemetryPanel({ telemetry = {}, droneId = null }) {
    if (!droneId) {
        return (
            <div className="panel-border" style={{
                background: 'var(--stitch-surface-lowest)',
                borderRadius: '12px',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--textMuted)',
                fontSize: '13px',
                fontStyle: 'italic',
            }}>
                Select a drone to view telemetry
            </div>
        );
    }

    const lat = parseFloat(telemetry.lat) || 0;
    const lon = parseFloat(telemetry.lon) || 0;
    const parsedRelativeAlt = parseFloat(telemetry.relative_alt);
    const parsedFallbackAlt = parseFloat(telemetry.alt);
    const parsedAltMSL = parseFloat(telemetry.alt_msl);
    const relativeAlt = Number.isFinite(parsedRelativeAlt)
        ? parsedRelativeAlt
        : (Number.isFinite(parsedFallbackAlt) ? parsedFallbackAlt : 0);
    const altMSL = Number.isFinite(parsedAltMSL)
        ? parsedAltMSL
        : (Number.isFinite(parsedFallbackAlt) ? parsedFallbackAlt : 0);
    const heading = parseFloat(telemetry.heading) || 0;

    const roll = parseFloat(telemetry.roll) || 0;
    const pitch = parseFloat(telemetry.pitch) || 0;
    const yaw = parseFloat(telemetry.yaw) || 0;

    const gpsFix = parseInt(telemetry.gps_fix) || 0;
    const satellites = parseInt(telemetry.satellites) || 0;

    const batteryVoltage = parseFloat(telemetry.battery_voltage) || 0;
    const batteryRemaining = parseFloat(telemetry.battery_remaining) || 0;

    const groundspeed = parseFloat(telemetry.groundspeed) || 0;
    const airspeed = parseFloat(telemetry.airspeed) || 0;

    const connected = telemetry.connected || false;
    const mode = telemetry.mode || 'N/A';
    const armed = telemetry.armed || false;

    const estimatedFlightTime = Math.round((batteryRemaining / 100) * 20);

    const rollDeg = roll * (180 / Math.PI);
    const pitchDeg = pitch * (180 / Math.PI);
    const yawDeg = yaw * (180 / Math.PI);

    const Cell = ({ label, value, unit = '', color, alert = false }) => (
        <div className="telemetry-card" style={{
            padding: '10px',
            borderRadius: '8px',
            background: alert ? 'rgba(239, 68, 68, 0.08)' : undefined,
            borderColor: alert ? 'var(--danger)' : undefined,
        }}>
            <p style={{
                fontFamily: 'var(--font-label)',
                fontSize: '10px',
                color: 'var(--textSecondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '4px',
            }}>
                {label}
            </p>
            <p style={{
                fontSize: '17px',
                fontWeight: 600,
                lineHeight: 1.1,
                color: color || 'var(--accent)',
            }}>
                {value}<span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--textSecondary)', marginLeft: '2px' }}>{unit}</span>
            </p>
        </div>
    );

    const SectionHead = ({ icon, title }) => (
        <h4 style={{
            fontFamily: 'var(--font-label)',
            fontSize: '12px',
            color: 'var(--textSecondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        }}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>{icon}</span>
            {title}
        </h4>
    );

    return (
        <div className="panel-border custom-scrollbar" style={{
            background: 'var(--stitch-surface-lowest)',
            borderRadius: '12px',
            padding: '16px',
            height: '100%',
            overflowY: 'auto',
        }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: 'var(--accentSecondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minWidth: 0,
                }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>analytics</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>TELEMETRY — {droneId}</span>
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px' }}>
                    <span style={{ color: 'var(--textSecondary)' }}>Mode: <span style={{ color: 'var(--textPrimary)', fontWeight: 700 }}>{mode}</span></span>
                    <span style={{ color: 'var(--textSecondary)' }}>Armed: <span style={{ color: armed ? 'var(--danger)' : 'var(--success)', fontWeight: 700 }}>{armed ? 'YES' : 'NO'}</span></span>
                </div>
            </div>

            {/* Position & Navigation */}
            <div style={{ marginBottom: '16px' }}>
                <SectionHead icon="location_on" title="Position & Navigation" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    <Cell label="Latitude" value={lat.toFixed(6)} unit="°" />
                    <Cell label="Longitude" value={lon.toFixed(6)} unit="°" />
                    <Cell label="Alt (AGL)" value={relativeAlt.toFixed(1)} unit=" m" />
                    <Cell label="Alt (MSL)" value={altMSL.toFixed(1)} unit=" m" />
                    <Cell label="Gr Speed" value={groundspeed.toFixed(1)} unit=" m/s" />
                    <Cell label="Air Speed" value={airspeed.toFixed(1)} unit=" m/s" />
                    <Cell label="Heading" value={heading.toFixed(0)} unit="°" />
                    <Cell label="Mode" value={mode} color="var(--accentSecondary)" />
                </div>
            </div>

            {/* Attitude */}
            <div style={{ marginBottom: '16px' }}>
                <SectionHead icon="explore" title="Attitude" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    <Cell label="Roll" value={rollDeg.toFixed(1)} unit="°" color={Math.abs(rollDeg) > 30 ? 'var(--warning)' : 'var(--accent)'} />
                    <Cell label="Pitch" value={pitchDeg.toFixed(1)} unit="°" color={Math.abs(pitchDeg) > 30 ? 'var(--warning)' : 'var(--accent)'} />
                    <Cell label="Yaw" value={yawDeg.toFixed(1)} unit="°" />
                </div>
            </div>

            {/* Power & Link */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                    <SectionHead icon="bolt" title="Power & Battery" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <Cell label="Voltage" value={batteryVoltage.toFixed(1)} unit=" V"
                            color={batteryVoltage < 10.5 ? 'var(--danger)' : 'var(--accent)'} alert={batteryVoltage < 10.5} />
                        <Cell label="Remaining" value={batteryRemaining.toFixed(0)} unit="%"
                            color={batteryRemaining < 20 ? 'var(--danger)' : batteryRemaining < 50 ? 'var(--warning)' : 'var(--accentSecondary)'}
                            alert={batteryRemaining < 20} />
                        <Cell label="Time Est" value={estimatedFlightTime} unit=" min"
                            color={estimatedFlightTime < 5 ? 'var(--danger)' : 'var(--accent)'} />
                    </div>
                </div>
                <div>
                    <SectionHead icon="sensors" title="Link & GPS" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <Cell label="GPS Fix" value={gpsFix >= 3 ? '3D Fix' : gpsFix === 2 ? '2D' : 'No Fix'}
                            color={gpsFix >= 3 ? 'var(--success)' : 'var(--danger)'} alert={gpsFix < 3} />
                        <Cell label="Satellites" value={satellites}
                            color={satellites >= 10 ? 'var(--success)' : satellites >= 6 ? 'var(--warning)' : 'var(--danger)'} alert={satellites < 6} />
                        <Cell label="Link" value={connected ? 'ONLINE' : 'OFFLINE'}
                            color={connected ? 'var(--success)' : 'var(--danger)'} alert={!connected} />
                        <Cell label="Signal" value={connected ? '100%' : '0%'}
                            color={connected ? 'var(--success)' : 'var(--danger)'} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default React.memo(DetailedTelemetryPanel);
